import { PQ_KEY_PREFIX } from "./hpkeFormat";

interface ExampleConnector {
  identifier: string;
  urlPattern: string;
}

export function connectorEncryptionScript(
  publicKey: string,
  fingerprint: string,
  connector?: ExampleConnector
) {
  if (publicKey.startsWith(PQ_KEY_PREFIX)) {
    if (!connector) throw new Error("A connector is required for PQ sealing");
    return `import { readFile } from "node:fs/promises";
import { preferredConnectorPublicKey, sealConnectorCredentials } from "./3b-connector-seal.mjs";

const key = JSON.parse(await readFile(process.argv[2], "utf8"));
const body = await sealConnectorCredentials({
  tenant_id: key.tenant_id,
  identifier: ${JSON.stringify(connector.identifier)},
  url_pattern: ${JSON.stringify(connector.urlPattern)},
  configuration: { authMethod: "bearer" },
  secrets: { apiKey: "your-secret" },
}, preferredConnectorPublicKey(key));
process.stdout.write(JSON.stringify(body, null, 2) + "\\n");`;
  }
  return `import * as HPKE from "hpke";

const plaintextSecrets = { apiKey: "your-secret" };
const suite = new HPKE.CipherSuite(
  HPKE.KEM_DHKEM_P256_HKDF_SHA256,
  HPKE.KDF_HKDF_SHA256,
  HPKE.AEAD_AES_256_GCM,
);
const publicKey = await suite.DeserializePublicKey(
  Buffer.from(${JSON.stringify(publicKey)}, "base64"),
);
const secrets = {};

for (const [field, value] of Object.entries(plaintextSecrets)) {
  const { encapsulatedSecret, ctx } = await suite.SetupSender(publicKey);
  const ciphertext = await ctx.Seal(new TextEncoder().encode(value));
  const commitment = await ctx.Export(
    new TextEncoder().encode("credential-commitment"),
    32,
  );
  secrets[field] = [
    "$hpke$1",
    ${JSON.stringify(fingerprint)},
    ...[encapsulatedSecret, ciphertext, commitment].map((bytes) =>
      Buffer.from(bytes).toString("base64"),
    ),
  ].join("$");
}

process.stdout.write(JSON.stringify(secrets, null, 2) + "\\n");`;
}

export function connectorEncryptionCommand(
  publicKey: string,
  fingerprint: string,
  connector?: ExampleConnector,
  moduleUrl?: string
) {
  if (publicKey.startsWith(PQ_KEY_PREFIX)) {
    if (!moduleUrl) throw new Error("Provide the connector sealer URL");
    return `cd "$(mktemp -d)"
curl --proto '=https' --tlsv1.2 -fsSLo 3b-connector-seal.mjs ${JSON.stringify(moduleUrl)}
cat > encrypt.mjs <<'EOF'
${connectorEncryptionScript(publicKey, fingerprint, connector)}
EOF
node encrypt.mjs /path/to/key.json`;
  }
  return `cd "$(mktemp -d)"
npm install --silent hpke@1.1.3
cat > encrypt.mjs <<'EOF'
${connectorEncryptionScript(publicKey, fingerprint)}
EOF
node encrypt.mjs`;
}
